import { Link } from 'react-router-dom';
import { ArrowRight, Mail } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import SecretaryDigestWidget from '@/components/dashboard/SecretaryDigestWidget';
import PendingRepliesWidget from '@/components/dashboard/PendingRepliesWidget';
import EventsWidget from '@/components/dashboard/EventsWidget';
import { useEmails } from '@/hooks/useEmails';
import { formatRelativeTime, formatEmailAddress, getCategoryColor, formatCategory } from '@/utils/formatters';

export default function Dashboard() {
  const { data: recentEmails, isLoading: emailsLoading } = useEmails({ limit: 10, order_by: 'date', order_dir: 'DESC' });

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Dashboard</h1>
        <p className="hidden sm:block text-sm text-muted-foreground mt-1">
          An intelligent brief of your inbox
        </p>
      </div>

      {/* Secretary Digest */}
      <SecretaryDigestWidget />

      {/* Secretary Widgets */}
      <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
        <PendingRepliesWidget />
        <EventsWidget />
      </div>

      {/* Recent Emails */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Recent Emails</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Your latest processed emails</p>
          </div>
          <Link
            to="/emails"
            className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            View all
            <ArrowRight className="h-4 w-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {emailsLoading ? (
            <div className="py-12">
              <LoadingSpinner />
            </div>
          ) : recentEmails && recentEmails.length > 0 ? (
            <div className="space-y-3">
              {recentEmails.map((email) => (
                <Link
                  key={email.id}
                  to={`/emails?id=${email.id}`}
                  className="block p-4 rounded-lg border hover:bg-accent transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium truncate">{formatEmailAddress(email.from_address)}</p>
                        {email.action_needed && (
                          <Badge variant="warning" className="text-xs">Action</Badge>
                        )}
                        {email.starred && (
                          <span className="text-yellow-500">★</span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-foreground truncate">{email.subject}</p>
                      {email.summary && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {email.summary}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        {email.category && (
                          <Badge className={getCategoryColor(email.category)}>
                            {formatCategory(email.category)}
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(email.date)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                No emails yet. Configure an account to get started.
              </p>
              <Link to="/accounts" className="text-sm text-primary hover:underline mt-2 inline-block">
                Add Account
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
